import { useState, useMemo } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { useCalendars } from '@/hooks/useCalendar';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { Tooltip } from '@/components/ui/Tooltip';

/**
 * CalendarSelector Component
 *
 * Dropdown for selecting calendar(s)
 * Supports both single and multi-select modes
 */

interface CalendarSelectorProps {
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  multiple?: boolean;
  label?: string;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
  /** Personal pinned calendar id (optional; enables the pin affordance) */
  pinnedId?: number | null;
  /** Called with the calendar id to pin, or null to unpin */
  onPinChange?: (id: number | null) => void;
}

export const CalendarSelector = ({
  selectedIds,
  onSelectionChange,
  multiple = true,
  label,
  error,
  disabled,
  placeholder,
  pinnedId = null,
  onPinChange,
}: CalendarSelectorProps) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const { data: calendars, isLoading } = useCalendars({ isActive: true });

  const defaultPlaceholder = placeholder || t('select_calendar_placeholder');

  // Get selected calendar names for display
  const selectedText = useMemo(() => {
    if (!calendars || selectedIds.length === 0) {
      return defaultPlaceholder;
    }

    const selectedCalendars = calendars.filter((cal) => selectedIds.includes(cal.id));
    if (selectedCalendars.length === 0) {
      return defaultPlaceholder;
    }

    if (selectedCalendars.length === 1) {
      return selectedCalendars[0].name;
    }

    return t('calendars_selected', { count: selectedCalendars.length });
  }, [calendars, selectedIds, defaultPlaceholder, t]);

  // Pinned calendar first, the rest keep their current order (stable)
  const sortedCalendars = useMemo(() => {
    if (!calendars) return calendars;
    if (pinnedId === null) return calendars;
    const pinned = calendars.filter((cal) => cal.id === pinnedId);
    if (pinned.length === 0) return calendars;
    return [...pinned, ...calendars.filter((cal) => cal.id !== pinnedId)];
  }, [calendars, pinnedId]);

  // Handle selection toggle
  const handleToggle = (calendarId: number) => {
    if (multiple) {
      // Toggle selection in multi-select mode
      if (selectedIds.includes(calendarId)) {
        // Unchecking is free (callers handle the empty state)
        onSelectionChange(selectedIds.filter((id) => id !== calendarId));
      } else {
        onSelectionChange([...selectedIds, calendarId]);
      }
    } else {
      // Replace selection in single-select mode
      onSelectionChange([calendarId]);
      setOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full">
        {label && <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>}
        <div className="px-4 py-3 border border-gray-200 rounded-lg bg-gray-50">
          <span className="text-sm text-gray-500">{t('loading_calendars')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full sm:w-[250px]">
      {label && <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>}

      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger
          disabled={disabled || !calendars || calendars.length === 0}
          className={cn(
            'w-full px-3 py-2 rounded-lg border border-gray-200',
            'flex items-center justify-between gap-2',
            'text-sm text-gray-700',
            'focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent',
            'transition-all duration-200',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            error && 'border-danger'
          )}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* Show color indicators for selected calendars */}
            {calendars && selectedIds.length > 0 && (
              <div className="flex items-center gap-1">
                {calendars
                  .filter((cal) => selectedIds.includes(cal.id))
                  .slice(0, 3)
                  .map((cal) => (
                    <Tooltip content={cal.name} key={cal.id}>
                      <div
                        className="w-3 h-3 rounded-full border border-gray-300"
                        style={{ backgroundColor: cal.color }}
                      />
                    </Tooltip>
                  ))}
                {selectedIds.length > 3 && (
                  <span className="text-xs text-gray-500">+{selectedIds.length - 3}</span>
                )}
              </div>
            )}
            <span className="truncate">{selectedText}</span>
          </div>
          <Icon name={ICONS.ANGLE_DOWN} size="sm" color="text-gray-400" ariaHidden />
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className={cn(
              'overflow-hidden bg-white rounded-lg shadow-xl border border-gray-200',
              'z-popover-top w-[var(--radix-popover-trigger-width)] min-w-[200px]'
            )}
            sideOffset={5}
            align="start"
          >
            <div className="p-2 max-h-[300px] overflow-y-auto">
              {sortedCalendars && sortedCalendars.length > 0 ? (
                sortedCalendars.map((calendar) => {
                  const isSelected = selectedIds.includes(calendar.id);
                  const isPinned = pinnedId === calendar.id;
                  return (
                    <div
                      key={calendar.id}
                      className={cn(
                        'w-full flex items-center gap-1 rounded',
                        'hover:bg-gray-100 focus-within:bg-gray-100',
                        isSelected && 'bg-primary-50',
                        'transition-colors duration-150'
                      )}
                    >
                    <button
                      type="button"
                      onClick={() => handleToggle(calendar.id)}
                      className={cn(
                        'flex-1 min-w-0 flex items-center gap-3 pl-3 pr-1 py-2 rounded',
                        'text-sm text-gray-700 text-left',
                        'outline-none',
                        'focus:bg-gray-100'
                      )}
                    >
                      {/* Checkbox indicator */}
                      <div
                        className={cn(
                          'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                          isSelected
                            ? 'bg-primary-500 border-primary-500'
                            : 'border-gray-300'
                        )}
                      >
                        {isSelected && (
                          <Icon name={ICONS.CHECK} size="xs" color="text-white" ariaHidden />
                        )}
                      </div>
                      {/* Calendar color */}
                      <div
                        className="w-4 h-4 rounded border border-gray-300 flex-shrink-0"
                        style={{ backgroundColor: calendar.color }}
                      />
                      {/* Calendar name */}
                      <span className="flex-1 truncate">{calendar.name}</span>
                      {isPinned && (
                        <span className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-primary-50 border border-primary-200 px-1.5 py-0.5 text-[10px] font-medium text-primary-700">
                          <Icon name={ICONS.PIN} size="xs" color="text-primary-600" ariaHidden />
                          {t('default_calendar_badge', { defaultValue: 'Default' })}
                        </span>
                      )}
                    </button>
                    {onPinChange && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onPinChange(isPinned ? null : calendar.id);
                        }}
                        aria-pressed={isPinned}
                        aria-label={
                          isPinned
                            ? t('unpin_calendar', { defaultValue: 'Unpin default calendar' })
                            : t('pin_calendar', { defaultValue: 'Pin as my default calendar' })
                        }
                        title={
                          isPinned
                            ? t('unpin_calendar', { defaultValue: 'Unpin default calendar' })
                            : t('pin_calendar', { defaultValue: 'Pin as my default calendar' })
                        }
                        className="group/pin flex-shrink-0 p-1 outline-none"
                      >
                        <Icon
                          name={ICONS.PIN}
                          size="sm"
                          color={isPinned ? 'text-primary-600' : 'text-gray-400'}
                          className="transition-colors group-hover/pin:text-primary-600"
                          ariaHidden
                        />
                      </button>
                    )}
                    </div>
                  );
                })
              ) : (
                <div className="px-3 py-4 text-sm text-gray-500 text-center">
                  {t('no_calendars_available')}
                </div>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {error && (
        <p className="mt-1 text-xs text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
